import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { ApplicationState } from 'store';
import { AiManagementBoardTask, AiManagementState } from 'store/modules/ai_management_store/types';
import { getTasks, moveTaskLocal } from 'store/modules/ai_management_store/actions';
import { updateTask } from 'services/actions/ai_management_tasks';
import AiManagementTaskBoardCard from 'components/molecules/ai_management_task_board_card';
import AiManagementTaskDrawer from 'components/organisms/ai_management_task_drawer';
import { Board, Column } from './styles';

const BOTTOM_GAP = 12;
const MIN_BOARD_HEIGHT = 320;

const AiManagementTasksKanban: React.FC = () => {
  const dispatch = useDispatch();
  const { taskColumns, tasks, request } = useSelector<ApplicationState, AiManagementState>(
    (state) => state.session.ai_management,
  );
  const boardRef = useRef<HTMLDivElement | null>(null);
  const [boardHeight, setBoardHeight] = useState<number | undefined>(undefined);
  const [openTask, setOpenTask] = useState<AiManagementBoardTask | null>(null);

  useEffect(() => {
    dispatch(getTasks());
  }, [dispatch]);

  const updateHeight = useCallback(() => {
    if (!boardRef.current) return;
    const { top } = boardRef.current.getBoundingClientRect();
    const available = Math.round(window.innerHeight - top - BOTTOM_GAP);
    setBoardHeight(Math.max(available, MIN_BOARD_HEIGHT));
  }, []);

  useEffect(() => {
    if (!request.tasks.loaded) return undefined;

    updateHeight();
    window.addEventListener('resize', updateHeight);
    window.addEventListener('orientationchange', updateHeight);
    const observer = new ResizeObserver(updateHeight);
    observer.observe(document.body);

    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('orientationchange', updateHeight);
      observer.disconnect();
    };
  }, [request.tasks.loaded, updateHeight]);

  const orderedColumns = useMemo(
    () => [...taskColumns].sort((a, b) => a.sort_group - b.sort_group || a.position - b.position),
    [taskColumns],
  );

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    const fromColumnId = Number(source.droppableId.split(':')[1]);
    const toColumnId = Number(destination.droppableId.split(':')[1]);
    if (fromColumnId === toColumnId) return;

    const taskId = Number(draggableId.split(':')[1]);

    dispatch(moveTaskLocal({ taskId, fromColumnId, toColumnId, toIndex: destination.index }));
    updateTask(taskId, { task_column_id: toColumnId }).then(() => dispatch(getTasks()));
  };

  if (!request.tasks.loaded && !request.tasks.error) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Carregando...</span>
        </div>
      </div>
    );
  }

  if (request.tasks.error) {
    return (
      <div className="text-center py-5">
        <p className="text-muted mb-2">Não foi possível carregar as tarefas.</p>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={() => dispatch(getTasks())}
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  if (orderedColumns.length === 0) {
    return (
      <div className="text-center py-5">
        <p className="text-muted mb-1">Nenhuma tarefa por aqui ainda.</p>
        <p className="text-muted small mb-0">
          Abra uma licitação em Minhas Licitações e use &quot;Adicionar tarefa&quot; para criar a
          primeira.
        </p>
      </div>
    );
  }

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Board ref={boardRef} boardHeight={boardHeight} className="d-flex overflow-auto pb-2">
          {orderedColumns.map((column) => {
            const columnTasks = [...(tasks[column.id] ?? [])].sort(
              (a, b) => a.position - b.position,
            );
            return (
              <Column
                key={column.id}
                barColor={column.color}
                className="flex-shrink-0 mr-3 rounded bg-white d-flex flex-column"
              >
                <div className="d-flex align-items-center px-3 py-2">
                  <span className="font-weight-bold text-dark text-truncate">{column.name}</span>
                  <span className="badge badge-light badge-pill border ml-2">
                    {column.tasks_count}
                  </span>
                </div>

                <Droppable droppableId={`tasks:${column.id}`}>
                  {(dropProvided, dropSnapshot) => (
                    <div
                      ref={dropProvided.innerRef}
                      {...dropProvided.droppableProps}
                      className="px-3 pb-3 flex-grow-1 overflow-auto"
                    >
                      {columnTasks.length === 0 && !dropSnapshot.isDraggingOver && (
                        <div className="d-flex align-items-center justify-content-center text-muted small rounded column-empty">
                          Arraste as tarefas aqui
                        </div>
                      )}
                      {columnTasks.map((task, index) => (
                        <Draggable key={task.id} draggableId={`task:${task.id}`} index={index}>
                          {(dragProvided, dragSnapshot) => (
                            <AiManagementTaskBoardCard
                              task={task}
                              provided={dragProvided}
                              isDragging={dragSnapshot.isDragging}
                              onOpen={setOpenTask}
                            />
                          )}
                        </Draggable>
                      ))}
                      {dropProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </Column>
            );
          })}
        </Board>
      </DragDropContext>

      {openTask && <AiManagementTaskDrawer task={openTask} onClose={() => setOpenTask(null)} />}
    </>
  );
};

export default AiManagementTasksKanban;
